import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getSocket } from "../../utils/socket/socketClient";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils"; // new

// helper to emit pos_update with mart/company context
const emitPosUpdate = (items: any[]) => {
  // try to read martId/companyId from decrypted token
  const martId = getDecryptedToken("selectedMartId") || "";
  const companyId =
    getDecryptedToken("companyId") || getDecryptedToken("companyID") || "";
  getSocket().emit("pos_update", { items, martId, companyId });
};

// helper to sync cart to BroadcastChannel for CustomerView
const syncCartToBroadcastChannel = (
  items: any[],
  commonDiscount: number | string = 0,
) => {
  try {
    // Transform POS items to CustomerView cart format
    const cartItems = items.map((item: any) => ({
      id: item.id || item.itemId || String(item.itemId),
      name: item.name || item.itemName || "Unknown Item",
      qty: Number(item.quantity) || 1,
      total: Number(item.finalPrice) || 0,
    }));

    // Calculate grand total
    const itemsTotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = Number(commonDiscount) || 0;
    const grandTotal = Math.max(0, itemsTotal - discountAmount);

    const cart = {
      items: cartItems,
      grandTotal: grandTotal,
    };

    // Send to BroadcastChannel
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("pos-sync");
      channel.postMessage(cart);
      channel.close();
    }
  } catch (error) {
    // Silently fail if BroadcastChannel is not available
    console.log("BroadcastChannel sync failed:", error);
  }
};

export interface PosCurrentState {
  addedItemArray: any[];
  card: number | string;
  cash: number | string;
  bank: number | string;
  commonDiscount: number | string;
  commonDiscountType: "amount" | "percent";
  billNumber: string;
  // Add customer fields
  customerId: number | null;
  customerName: string;
  customerPhone: string;
  customerType: string;
  // Add sale type field
  saleType: "cash" | "credit";
}

const initialCurrentState: PosCurrentState = {
  addedItemArray: [],
  card: "",
  cash: "",
  bank: "",
  commonDiscount: "",
  commonDiscountType: "amount",
  billNumber: "",
  // Initialize customer fields
  customerId: null,
  customerName: "",
  customerPhone: "",
  customerType: "",
  // Initialize sale type
  saleType: "cash",
};

const initialState: any = {
  holed: [],
  current: initialCurrentState,
  activeInput: "search",
  activeInputId: "",
  search: "",
  isSelected: false,
  openHold: false,
  allItems: [],
  barcode: "",
  saleDate: null, // ISO string for backdated sale, null = current time
  defaultTaxEnabled: false, // whether the default tax toggle is on
  defaultTaxPercentage: null, // the mart's configured default tax %, null means not set
};

export const posSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    setHold: (state, action) => {
      state.openHold = action.payload;
    },
    setAllItems: (state, action) => {
      state.allItems = action.payload;
    },

    addItem: (state, action) => {
      const now = new Date();
      // If item.modified === true, skip stock check
      if (
        !action.payload.modified &&
        (action.payload.stock === 0 || action.payload.stock < 0)
      ) {
        toast("out of stock");
        return;
      }
      // Always assign a unique id (ignore provided id)
      let uniqueId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : now.toISOString() + "_" + Math.random().toString(36).slice(2);
      state.current.addedItemArray.push({
        ...action.payload,
        itemId: action.payload.itemId || action.payload.id,
        id: uniqueId,
        quantity: action.payload.quantity || 1,
        discount: `${action.payload.discount || 0}`,
        finalPrice:
          (action.payload.price || 0) * (action.payload.quantity || 1) -
          (action.payload.discount || 0),
        dineIn: !!action.payload.dineIn,
      });
      // emit with context
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    addCustomItem: (state, action) => {
      let productData: any = {};
      if (action.payload.itemId) {
        const found = state.allItems.find(
          (item: any) => String(item.id) === String(action.payload.itemId),
        );
        if (found) {
          productData = { ...found };
        }
      }
      const now = new Date();
      const timeString = now.toISOString();
      state.current.addedItemArray.push({
        ...productData,
        ...action.payload,
        id: timeString,
        quantity: action.payload.quantity || 1,
        price: action.payload.price || productData.price || 0,
        discount: `${action.payload.discount || 0}`,
        finalPrice:
          (action.payload.price || productData.price || 0) *
            (action.payload.quantity || 1) -
          (action.payload.discount || 0),
        isCustom: true,
      });
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    // New function to only increase the quantity of the clicked item
    increaseItemQuantity: (state, action) => {
      const { itemId } = action.payload;
      const idx = state.current.addedItemArray.findIndex(
        (item: any) => item.itemId === itemId,
      );
      const item = state.current.addedItemArray[idx];

      // If item.modified === true, skip stock check
      if (item && (item.modified === true || item.quantity < item.stock)) {
        item.quantity += 1;
        item.finalPrice =
          item.price * item.quantity - (item.discount * item.quantity || 0);

        // Move updated existing item to the end so it appears as the latest added.
        state.current.addedItemArray.splice(idx, 1);
        state.current.addedItemArray.push(item);
      }
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    removeItem: (state, action) => {
      state.current.addedItemArray = state.current.addedItemArray.filter(
        (item: any) => item.id !== action.payload,
      );
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    updateDiscount: (state, action) => {
      const { id, value } = action.payload;
      if (value !== "" && !/^[0-9]+$/.test(value)) {
        return;
      }

      const item = state.current.addedItemArray.find(
        (item: any) => item.id === id,
      );

      if (item) {
        item.discountexceed = false;
        const qty = parseFloat(item.quantity) || 0;
        if (parseInt(value || "0") <= parseInt(item.price)) {
          item.discount = value;
          item.finalPrice = item.price * qty - (parseInt(value) * qty || 0);
          item.discountexceed = true;
        } else {
          item.discountexceed = false;
        }
      }
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    updateQty: (state, action) => {
      const { id, actions } = action.payload;

      const idx = state.current.addedItemArray.findIndex(
        (item: any) => item.id === id,
      );
      const item = state.current.addedItemArray[idx];

      if (item) {
        if (actions === "increase") {
          if (item.modified === true || item.quantity < item.stock) {
            item.finalPrice =
              item.price * (item.quantity + 1) -
              (item.discount * (item.quantity + 1) || 0);
            item.quantity = parseInt(item.quantity) + 1;
            // Move updated existing item to the end so it appears as the latest added.
            state.current.addedItemArray.splice(idx, 1);
            state.current.addedItemArray.push(item);
          }
        } else if (actions === "decrease" && item.quantity > 1) {
          item.finalPrice =
            item.price * (item.quantity - 1) -
            (item.discount * (item.quantity - 1) || 0);
          item.quantity -= 1;
          // Keep order behavior consistent for quantity updates.
          state.current.addedItemArray.splice(idx, 1);
          state.current.addedItemArray.push(item);
        }
      }
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    changeQty: (state, action) => {
      const { id, value, scale } = action.payload;

      // Allow decimal input if scale is not "unit"
      const isDecimalAllowed = scale !== "Unit";
      const regex = isDecimalAllowed ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]+$/;

      if (!regex.test(value)) {
        return;
      }

      const item = state.current.addedItemArray.find(
        (item: any) => item.id === id,
      );
      if (item) {
        const numValue = isDecimalAllowed ? parseFloat(value) : parseInt(value);

        // If modified, skip stock check
        if (item.modified === true || numValue <= item.stock) {
          item.finalPrice =
            item.price * numValue - (item.discount * numValue || 0);
          item.quantity = value;
          item.discount = 0;

          // Move updated existing item to the end so it appears as the latest added.
          const idx = state.current.addedItemArray.findIndex(
            (i: any) => i.id === id,
          );
          if (idx > -1) {
            state.current.addedItemArray.splice(idx, 1);
            state.current.addedItemArray.push(item);
          }
        }
      }
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    posChange: (state, action) => {
      const { name, value } = action.payload;

      if (state.current.addedItemArray.length > 0) {
        const isBillDiscount = name === "commonDiscount";
        const billDiscountRegex = /^\d*\.?\d*$/;

        if (
          value === "" ||
          (isBillDiscount
            ? billDiscountRegex.test(String(value))
            : /^[0-9]*\.?[0-9]*$/.test(String(value)))
        ) {
          if (
            isBillDiscount &&
            state.current.commonDiscountType === "percent" &&
            Number(value || 0) > 100
          ) {
            state.current[name] = "100";
          } else {
            state.current[name] = value;
          }
        }
      } else {
        toast("please add items");
      }
      emitPosUpdate(
        state.current.addedItemArray.map((item: any) => ({ ...item })),
      );
      // sync to BroadcastChannel (especially for commonDiscount changes)
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
    posCommonChange: (state, action) => {
      console.log(initialState);
      const { name, value } = action.payload;
      state.current[name] = value;
    },
    setCommonDiscountType: (state, action) => {
      const mode = action.payload as "amount" | "percent";
      state.current.commonDiscountType = mode;

      if (mode === "percent") {
        const currentValue = Number(state.current.commonDiscount || 0);
        if (currentValue > 100) {
          state.current.commonDiscount = "100";
        }
      }
    },
    posSearch: (state, action) => {
      console.log(action.payload);

      const { name, value } = action.payload;
      state[name] = value;
    },
    posCharButton: (state, action) => {
      console.log(action.payload, state.activeInput);

      if (state.activeInput === "common") {
        if (
          state.current.addedItemArray.length > 0 ||
          state.activeInputId === "search" ||
          state.activeInputId === "barcode"
        ) {
          if (
            /^[0-9a-zA-Z]+$/.test(action.payload) ||
            action.payload === " " ||
            action.payload === "." ||
            action.payload === "00"
          ) {
            const key = state.activeInputId as keyof typeof state;
            // If key is "search", update state.search
            if (key === "search") {
              state.search = (state.search || "") + action.payload.toString();
            } else if (key === "barcode") {
              state.barcode = (state.barcode || "") + action.payload.toString();
            } else {
              state.current[key] =
                state.current[key] + action.payload.toString();
            }
          }
        } else {
          toast("please add items");
        }
      } else if (state.activeInput === "data") {
        const key = state.activeInputId as keyof typeof state;
        state.current[key] = state.current[key] + action.payload.toString();
      } else if (state.activeInput === "qty") {
        const key = state.activeInputId as keyof typeof state;
        if (!/^[0-9]+$/.test(action.payload)) {
          return;
        }
        const item = state.current.addedItemArray.find(
          (item: any) => item.id === key,
        );
        if (item) {
          item.finalPrice = item.price * action.payload.toString();
          if (state.isSelected) {
            if (action.payload <= item.stock) {
              item.quantity = parseInt(action.payload);
              item.discount = 0;
              state.isSelected = false;
            }
          } else {
            if (
              parseInt(item.quantity + action.payload.toString()) <= item.stock
            ) {
              item.quantity = parseInt(
                item.quantity + action.payload.toString(),
              );
              item.discount = 0;
            }
          }
        }
      } else {
        const key = state.activeInputId as keyof typeof state;
        const item = state.current.addedItemArray.find(
          (item: any) => item.id === key,
        );
        if (item) {
          item.discountexceed = false;
          if (parseInt(action.payload) <= parseInt(item.finalPrice)) {
            item.discount = item.discount + action.payload.toString();
            item.discountexceed = true;
          } else {
            item.discountexceed = false;
          }
        }
      }
      // sync to BroadcastChannel if items were modified
      if (
        state.activeInput === "qty" ||
        (state.activeInput === "" && state.activeInputId)
      ) {
        syncCartToBroadcastChannel(
          state.current.addedItemArray,
          state.current.commonDiscount,
        );
      }
    },
    backSpace: (state) => {
      if (state.activeInput === "common") {
        const key = state.activeInputId as keyof typeof state;
        if (key === "search") {
          state.search = String(state.search).slice(0, -1);
        } else if (key === "barcode") {
          state.barcode = String(state.barcode).slice(0, -1);
        } else {
          const currentValue = String(state.current[key]);
          state.current[key] = currentValue.slice(0, -1);
        }
      } else if (state.activeInput === "data") {
        const key = state.activeInputId as keyof typeof state;
        const currentValue = String(state.current[key]);
        state.current[key] = currentValue.slice(0, -1);
      } else if (state.activeInput === "qty") {
        const key = state.activeInputId as keyof typeof state;
        const item = state.current.addedItemArray.find(
          (item: any) => item.id === key,
        );
        if (item) {
          const quantityStr = item.quantity?.toString(); // ensure it's a string

          const parsedQuantity = parseInt(quantityStr?.slice(0, -1) || "1", 10);

          item.finalPrice = item.price * parsedQuantity;
          item.quantity = parsedQuantity;
        }
      } else {
        const key = state.activeInputId as keyof typeof state;
        const item = state.current.addedItemArray.find(
          (item: any) => item.id === key,
        );
        if (item) {
          item.discountexceed = false;
          item.discount = item.discount?.slice(0, -1);
        }
      }
      // sync to BroadcastChannel if items were modified
      if (
        state.activeInput === "qty" ||
        (state.activeInput === "" && state.activeInputId)
      ) {
        syncCartToBroadcastChannel(
          state.current.addedItemArray,
          state.current.commonDiscount,
        );
      }
    },
    posInputFocus: (state, action) => {
      console.log(action.payload);

      const { name, value } = action.payload;
      state.activeInput = name;
      state.activeInputId = value;
      state.isSelected = true;
    },
    hold: (state) => {
      const dates = new Date();
      const curent = state.current;

      state.isSelected = true;
      state.activeInput = "search";
      state.activeInputId = "";
      state.search = "";
      state.holed = [...state.holed, curent];
      state.current = {
        addedItemArray: [],
        card: "",
        cash: "",
        bank: "",
        commonDiscount: "",
        commonDiscountType: "amount",
        id: dates.toISOString(),
      };
      // sync empty cart to BroadcastChannel
      syncCartToBroadcastChannel([], 0);
    },
    reCallById: (state, action) => {
      const found = state.holed.find((item: any) => item.id === action.payload);
      const newData = state.holed.filter(
        (item: any) => item.id !== action.payload,
      );
      state.isSelected = true;
      state.activeInput = "search";
      state.activeInputId = "";
      state.search = "";
      state.holed = newData;
      state.current = found;
      // sync recalled cart to BroadcastChannel
      if (found) {
        syncCartToBroadcastChannel(
          found.addedItemArray || [],
          found.commonDiscount || 0,
        );
      }
    },
    reCallTableByItemId: (state, action) => {
      const found = state.holed.find((item: any) => item.id === action.payload);
      const newData = state.holed.filter(
        (item: any) => item.id !== action.payload,
      );
      state.isSelected = true;
      state.activeInput = "search";
      state.activeInputId = "";
      state.search = "";
      state.holed = newData;
      state.current = found;
      // sync recalled cart to BroadcastChannel
      if (found) {
        syncCartToBroadcastChannel(
          found.addedItemArray || [],
          found.commonDiscount || 0,
        );
      }
    },
    reCall: (state) => {
      state.openHold = true;
    },
    // Add new reducer for selecting customer
    selectCustomer: (state, action) => {
      const customer = action.payload;
      state.current.customerId = customer.id;
      state.current.customerName = customer.name;
      state.current.customerPhone = customer.phoneNumber;
      state.current.customerType = customer.customerType;
    },
    // Add reducer to set sale type
    setSaleType: (state, action) => {
      state.current.saleType = action.payload;
    },
    // Add reducer to clear customer and reset sale type
    clearCustomer: (state) => {
      state.current.customerId = null;
      state.current.customerName = "";
      state.current.customerPhone = "";
      state.current.customerType = "";
      state.current.saleType = "cash"; // Reset to cash when customer is cleared
    },
    setSaleDate: (state, action) => {
      state.saleDate = action.payload; // ISO string or null
    },
    clearSaleDate: (state) => {
      state.saleDate = null;
    },
    setDefaultTaxPercentage: (state, action) => {
      // action.payload: number | null — the mart's configured default tax %
      state.defaultTaxPercentage = action.payload;
      state.defaultTaxEnabled = false; // reset toggle when mart changes
    },
    toggleDefaultTax: (state) => {
      if (state.defaultTaxPercentage != null) {
        state.defaultTaxEnabled = !state.defaultTaxEnabled;
      }
    },
    resetPos: (state) => {
      emitPosUpdate([]);
      // sync empty cart to BroadcastChannel
      syncCartToBroadcastChannel([], 0);
      // Reset state but preserve allItems, barcode, and defaultTaxPercentage across sales
      const preservedAllItems = state.allItems;
      const preservedDefaultTaxPercentage = state.defaultTaxPercentage;
      const newState = {
        ...initialState,
        allItems: preservedAllItems,
        defaultTaxPercentage: preservedDefaultTaxPercentage,
        defaultTaxEnabled: false, // reset toggle off for each new sale
      };
      return newState;
    },
    updateAddedItemArrayFromSocket: (state, action) => {
      state.current.addedItemArray = action.payload;
      // sync to BroadcastChannel
      syncCartToBroadcastChannel(
        state.current.addedItemArray,
        state.current.commonDiscount,
      );
    },
  },
});

export const {
  addItem,
  removeItem,
  updateDiscount,
  updateQty,
  posChange,
  resetPos,
  posSearch,
  posCharButton,
  posInputFocus,
  backSpace,
  changeQty,
  hold,
  reCallById,
  setHold,
  reCall,
  posCommonChange,
  increaseItemQuantity,
  setAllItems,
  selectCustomer,
  clearCustomer,
  setSaleType, // Export the new action
  addCustomItem,
  reCallTableByItemId,
  updateAddedItemArrayFromSocket,
  setSaleDate,
  clearSaleDate,
  setDefaultTaxPercentage,
  toggleDefaultTax,
  setCommonDiscountType,
} = posSlice.actions;

export default posSlice.reducer;
